import { useState, useEffect, useRef } from "react";
import useWebSocket from "react-use-websocket";

/** Tempo sem novas mensagens para considerar o stream encerrado (servidor pode não fechar a conexão). */
const STREAM_IDLE_MS = 1500;

export const useWebsocketFetch = ({ onSuccess, onError, onEmpty }) => {
  const [socketUrl, setSocketUrl] = useState(null);
  const [status, setStatus] = useState("off");
  const [data, setData] = useState([]);
  const dataRef = useRef([]);
  const statusRef = useRef(status);
  const streamIdleTimerRef = useRef(null);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onEmptyRef = useRef(onEmpty);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  onEmptyRef.current = onEmpty;

  dataRef.current = data;
  statusRef.current = status;

  const finishFetch = () => {
    if (streamIdleTimerRef.current) {
      clearTimeout(streamIdleTimerRef.current);
      streamIdleTimerRef.current = null;
    }
    const currentData = dataRef.current;
    if (currentData?.length !== 0) {
      onSuccessRef.current?.(currentData);
    }
    statusRef.current = "off";
    setStatus("off");
    dataRef.current = [];
    setData([]);
  };

  const onMessageReceive = (response) => {
    if (
      !response?.Result ||
      response?.message === "Forbidden" ||
      response?.Result[0]?.MessageResponse === "Nenhum registro localizado." ||
      response?.Result[0]?.MessageResponse === "Falha em validação de filtros!"
    ) {
      if (streamIdleTimerRef.current) {
        clearTimeout(streamIdleTimerRef.current);
        streamIdleTimerRef.current = null;
      }

      if (dataRef.current?.length > 0) {
        finishFetch();
        return;
      }
      setStatus("empty");
      setStatus("off");
      if (onEmptyRef.current) {
        onEmptyRef.current();
      }
      return;
    }
    const chunk = response?.Result ?? [];
    if (streamIdleTimerRef.current) {
      clearTimeout(streamIdleTimerRef.current);
    }
    streamIdleTimerRef.current = setTimeout(() => {
      streamIdleTimerRef.current = null;
      finishFetch();
    }, STREAM_IDLE_MS);
    setData((prevData) => {
      const next = [...prevData, ...chunk];
      dataRef.current = next;
      return next;
    });
  };

  const { sendJsonMessage } = useWebSocket(socketUrl, {
    onOpen: () => {
      if (streamIdleTimerRef.current) {
        clearTimeout(streamIdleTimerRef.current);
        streamIdleTimerRef.current = null;
      }
      setStatus("on");
      dataRef.current = [];
      setData([]);
    },
    onClose: () => {
      if (streamIdleTimerRef.current) {
        clearTimeout(streamIdleTimerRef.current);
        streamIdleTimerRef.current = null;
      }
      const currentData = dataRef.current;
      const currentStatus = statusRef.current;

      if (
        (currentData?.length === 0 && currentStatus === "fetching") ||
        (currentData?.length === 1 &&
          (currentData[0]?.MessageResponse === "Nenhum registro localizado." ||
            currentData[0]?.MessageResponse === "Falha em validação de filtros!"))
      ) {
        setStatus("empty");
        setStatus("off");
        dataRef.current = [];
        setData([]);
        return;
      }

      if (currentData?.length !== 0) {
        onSuccessRef.current?.(currentData);
        setStatus("off");
      }
      dataRef.current = [];
      setData([]);
    },
    onError: (error) => {
      setStatus("error");
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    },
    onMessage: (event) => {
      try {
        const response = JSON.parse(event.data);
        onMessageReceive(response);
      } catch {
        // frame não-JSON (ignorar)
      }
    },
    shouldReconnect: () => true,
    reconnectInterval: 2000,
  });

  const handleFetchData = (data) => {
    statusRef.current = "fetching";
    setStatus("fetching");
    // react-use-websocket enfileira quando o socket ainda não está OPEN e envia ao conectar.
    sendJsonMessage(data);
  };

  const handleFetchUrl = (url) => {
    setSocketUrl(url);
  };

  const cancel = () => {
    sendJsonMessage({ status: "inactive" });
  };

  useEffect(() => {
    return () => {
      if (streamIdleTimerRef.current) {
        clearTimeout(streamIdleTimerRef.current);
        streamIdleTimerRef.current = null;
      }
    };
  }, []);

  return {
    handleFetchData,
    handleFetchUrl,
    data,
    status,
    cancel,
  };
};
